import {Alert, Button, useAuthenticator, View} from '@aws-amplify/ui-react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {usePasskeyAuth} from '../../hooks/usePasskeyAuth'
import {useProgressiveAuth} from './progressive-auth-context'
import {GoPasskeyFill} from "react-icons/go";

/**
 * Custom Sign In Footer component with Passkey button
 * This replaces the DOM polling approach with proper Amplify slots
 */
export function SignInFooter() {
    const {toForgotPassword} = useAuthenticator()
    const {availableChallenges, isChallengeReady, setChallenges, resetChallenges} = useProgressiveAuth()
    const {
        handlePasskeySignIn,
        status: passkeyStatus,
        isLoading: isPasskeyLoading,
        clearStatus: clearPasskeyStatus
    } = usePasskeyAuth()
    const [email, setEmail] = useState('')
    const [isDiscovering, setIsDiscovering] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedMethod, setSelectedMethod] = useState<'password' | 'passkey' | null>(null)
    const sanitizedEmail = useMemo(() => email.toLowerCase().trim().replace(/\s+/g, ''), [email])
    const lastEmail = useRef('')
    const canUsePasskey = isChallengeReady && availableChallenges.includes('WEB_AUTHN')
    const shouldShowPasswordField = isChallengeReady && (!canUsePasskey || selectedMethod === 'password')

    useEffect(() => {
        // Listen for changes to the email input
        const checkEmailInput = () => {
            const emailInput = document.querySelector('input[name="username"]') as HTMLInputElement
            if (emailInput) {
                const currentEmail = emailInput.value.trim().toLowerCase()
                if (currentEmail !== email) {
                    setEmail(currentEmail)
                }
            }
        }

        // Use a small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            checkEmailInput()
        }, 100)

        // Set up event listener for input changes
        const emailInput = document.querySelector('input[name="username"]') as HTMLInputElement
        if (emailInput) {
            emailInput.addEventListener('input', checkEmailInput)
            emailInput.addEventListener('change', checkEmailInput)
            emailInput.addEventListener('blur', checkEmailInput)

            return () => {
                emailInput.removeEventListener('input', checkEmailInput)
                emailInput.removeEventListener('change', checkEmailInput)
                emailInput.removeEventListener('blur', checkEmailInput)
                clearTimeout(timeoutId)
            }
        }

        return () => clearTimeout(timeoutId)
    }, [email])

    useEffect(() => {
        if (lastEmail.current && lastEmail.current !== sanitizedEmail && isChallengeReady) {
            resetChallenges()
        }

        lastEmail.current = sanitizedEmail
    }, [sanitizedEmail, isChallengeReady, resetChallenges])

    useEffect(() => {
        if (!isChallengeReady) {
            setSelectedMethod(null)
        }
    }, [isChallengeReady])

    useEffect(() => {
        if (isChallengeReady && !canUsePasskey) {
            setSelectedMethod('password')
        }
    }, [canUsePasskey, isChallengeReady])

    useEffect(() => {
        const passwordField = document.querySelector('input[name="password"]')?.closest('[data-amplify-form-field], .amplify-field-group, .amplify-flex') as HTMLElement
        const submitButton = document.querySelector('form[data-amplify-authenticator-signin] button[type="submit"]') as HTMLButtonElement

        if (passwordField) {
            passwordField.style.display = shouldShowPasswordField ? '' : 'none'
        }

        if (submitButton) {
            submitButton.style.display = shouldShowPasswordField ? '' : 'none'
            submitButton.disabled = !shouldShowPasswordField
        }
    }, [shouldShowPasswordField])

    const fetchChallenges = useCallback(async () => {
        if (!sanitizedEmail || !sanitizedEmail.includes('@')) {
            setError('Enter a valid email to continue')
            return
        }

        setIsDiscovering(true)
        setError(null)
        setSelectedMethod(null)

        const emailInput = document.querySelector('input[name="username"]') as HTMLInputElement
        if (emailInput) {
            emailInput.value = sanitizedEmail
        }

        try {
            const {signIn} = await import('aws-amplify/auth')
            const response = await signIn({
                username: sanitizedEmail,
                options: {
                    authFlowType: 'USER_AUTH' as const
                }
            })


            // @ts-ignore // it's okay
            const challenges = response.nextStep?.availableChallenges ?? []
            setChallenges(challenges, response.nextStep?.signInStep ?? null)
            setSelectedMethod(challenges.includes('WEB_AUTHN') ? null : 'password')
        } catch (err: any) {
            const message = err?.message || 'Could not check available sign-in methods'
            setError(message)
            resetChallenges()
        } finally {
            setIsDiscovering(false)
        }
    }, [resetChallenges, sanitizedEmail, setChallenges])

    const handleEmailFocus = () => {
        if (error) setError(null)
    }

    const chooseMethod = (method: 'password' | 'passkey') => {
        if (error) setError(null)

        if (method === 'password') {
            clearPasskeyStatus()
            setSelectedMethod('password')

            // Slight delay to allow Amplify to unhide the field
            setTimeout(() => {
                const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement
                if (passwordInput) {
                    passwordInput.focus()
                }
            }, 50)
            return
        }

        setSelectedMethod('passkey')
    }

    const tryPasskey = useCallback(async () => {
        if (!sanitizedEmail || !sanitizedEmail.includes('@')) {
            setError('Enter a valid email to continue')
            return
        }

        await handlePasskeySignIn(sanitizedEmail)
    }, [handlePasskeySignIn, sanitizedEmail])

    useEffect(() => {
        const form = document.querySelector('form[data-amplify-authenticator-signin]') as HTMLFormElement

        if (!form) return

        const handleSubmit = (event: Event) => {
            if (!isChallengeReady) {
                event.preventDefault()
                void fetchChallenges()
                return
            }

            if (email.includes("@") && canUsePasskey && !shouldShowPasswordField) {
                event.preventDefault()
                setError('Choose passkey or password to continue')
            }
        }

        form.addEventListener('submit', handleSubmit)

        return () => {
            form.removeEventListener('submit', handleSubmit)
        }
    }, [canUsePasskey, fetchChallenges, isChallengeReady, shouldShowPasswordField])

    return (
        <View>
            {!isChallengeReady && (
                <Button
                    width="100%"
                    onClick={fetchChallenges}
                    isLoading={isDiscovering}
                    loadingText="Checking options..."
                    variation="primary"
                    isDisabled={!sanitizedEmail || !sanitizedEmail.includes('@')}
                    className={'mt-0'}
                >
                    Continue
                </Button>
            )}

            {isChallengeReady && canUsePasskey && (selectedMethod === null || selectedMethod === 'passkey') && (
                <>
                    <View paddingTop="small" className={'flex flex-row gap-4 p-0'}>
                        <button
                            onClick={() => chooseMethod('password')}
                            className={'w-full rounded-md text-center bg-gray-100 py-2 flex flex-row items-center justify-center gap-4'}
                        >
                            Sign in with password
                        </button>
                        <button
                            onClick={() => {
                                chooseMethod('passkey')
                                void tryPasskey()
                            }}
                            disabled={isPasskeyLoading}
                            className={'w-full rounded-md text-center bg-gray-100 py-2 flex flex-row items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed'}
                    >
                            <GoPasskeyFill fill={'#444444'}/>
                            Sign in with passkey
                        </button>

                </View>
                    {!!['error'].includes(passkeyStatus?.type ?? '') && (
                        <Alert
                            variation="error"
                            marginTop="small"
                        >
                            {passkeyStatus?.message}
                        </Alert>
                    )}
                </>
            )}

            {shouldShowPasswordField && canUsePasskey && (
                <View textAlign="center" paddingTop="xsmall">
                    <Button
                        variation="link"
                        size="small"
                        onClick={() => {
                            chooseMethod('passkey')
                            void tryPasskey()
                        }}
                    >
                        Use a passkey instead
                    </Button>
                </View>
            )}

            {error && (
                <Alert
                    variation="error"
                    marginTop="small"
                >
                    {error}
                </Alert>
            )}

            <View textAlign="center" paddingTop="small">
                <Button
                    fontWeight="normal"
                    onClick={toForgotPassword}
                    size="small"
                    variation="link"
                    onFocus={handleEmailFocus}
                >
                    Forgot your password?
                </Button>
            </View>
        </View>
    )
}
